import {
    isObject,
    isArray,
    isFunction,
    isString,
    deepMixin,
    deepClone
} from "../../../funcs";
import Events from "../../Events";
import RowsStorage from "../../storage/RowsStorage";
import langDictionary from "../../lang/init";
import allGridsConfig from "../../grid/config";
import toolbarConfigFromFile from "../../grid/toolbar/config";
import rightsByType from "./rightsByType";
import RightsManager from "./RightsManager";

// Если не делать клон, то любая модификация повлияет на все гриды!
// что приведет к багу (лишние иконки в тех гридах, где ничего не должно быть)
const toolbarConfig = deepClone(toolbarConfigFromFile);

const ELEMENTS_CLASSES = [
    "Checkbox",
    "Input",
    "Number",
    "Phone",
    "Date",
    "Textarea",
    "GridList",
    "Grid",
    "Comments",
    "Address",
    "CheckboxList",
    "File",
    "Photo",
    "Minutes",
    "MarineMap",
    "LinkInput"
];

// LIST_TYPE_ROLE статичный справочник
// и у него стоит флаг @keep_in_memory
// который кэширует данные намертво
// @see "json!/api/dictionaries"
// @see scheme-generator/index.ts
const LIST_TYPE_ROLE = RowsStorage.get("LIST_TYPE_ROLE");

function getCompanyRoles() {
    return LIST_TYPE_ROLE.collection.map((companyRoleModel) => ( {
        id: companyRoleModel.id,
        name: companyRoleModel.get("LIST_TYPE_ROLE_NAME")
    } ));
}

// в таблице OPERATION_TYPE есть столбец id_company
// который редактировать разрешено,
// поэтому использовать @keep_in_memory не получится
const OPERATION_TYPE = RowsStorage.get("OPERATION_TYPE");
// данные будут загружены позже (@see init)
// т.к. на данный момент времени RowsStorage не готов их загружать

function getLogOperationTypeName(typeId) {
    let operationModel = OPERATION_TYPE.collection.get(typeId);
    if ( !operationModel ) {
        throw new Error(`unknown operation type: ${typeId}`);
    }

    return operationModel.get("NAME");
}


// @see json classes/system/rights/rightsByType
const ORIGINAL_TABLE_NAME_BY_DUPLICATE_NAME = {};
for (let originalEntityName in rightsByType) {
    let types = rightsByType[ originalEntityName ];

    types.forEach((type) => {
        let duplicateEntityName = type.TABLE;

        ORIGINAL_TABLE_NAME_BY_DUPLICATE_NAME[ duplicateEntityName ] = originalEntityName;
    });
}

let storageIndex = 0;
export default class RightsStorage extends Events {
    private _added: object;
    private _addedForms: object;
    name: string;
    storage: RowsStorage;

    constructor() {
        super();

        // @see getLogOperationTypeName
        OPERATION_TYPE.get({ limit: "all" });

        this.initStorage();
    }

    initStorage() {
        this.name = "RightsStorage" + (++storageIndex);
        this.storage = RowsStorage.createLocal(this.name, [
            {
                key: "ID",
                type: "text"
            },
            {
                key: "ID_PARENT",
                type: "text"
            },
            {
                key: "NAME",
                type: "text"
            },
            {
                key: "TABLE_NAME",
                type: "text"
            },
            {
                key: "FORM2_CACHE_NAME",
                type: "text"
            }
        ], []);
    }

    addTable(tableName, table) {
        const { noColumnRights, noToolbarRights } = table;

        if ( !noToolbarRights ) {
            this.addTableToolbar(tableName, table);
        }

        this.addTableFilters(tableName);

        if ( !noColumnRights ) {
            this.addTableColumns(tableName, table);
        }
    }

    addTableToolbar(tableName, table) {
        const toolbarId = tableName + ".toolbar";

        this.storage._addRowToCache({
            ID: toolbarId,
            NAME: langDictionary.get("rights_name__toolbar"),
            TABLE_NAME: tableName,
            ID_PARENT: null,
            FORM2_CACHE_NAME: null
        });

        const toolbar = table && table.toolbar || {};
        const kit = toolbar.kit || "default";

        const rows = this._toolbarIcons2rows(
            {
                toolbarId,
                tableName,
                kit
            }
        );

        if ( system.hasModule("Maersk") ) {
            this.addToolbarItemsForMaersk(rows, toolbarId, tableName);
        }

        rows.forEach((row) => {
            this.storage._addRowToCache(row);
        });
    }

    addFieldsForVoskhod(fields, tableName) {
        if ( tableName == "LIST_ACTIVE_GTD" || tableName == "LIST_ARCHIVE_GTD" || tableName == "LIST_ALL_GTD" ) {
            fields.push("USER_UNIT_GTD_FULL_NAME_TWO");
        }
    }

    addToolbarItemsForMaersk(rows, toolbarId, tableName) {
        if ( tableName === "ORDER" ) {
            const importId = `${toolbarId}.import`;

            if ( rows.some(({ ID }) => ID === importId) ) {
                return;
            }

            rows.push(
                {
                    ID: importId,
                    NAME: langDictionary.getIconTitle("import"),
                    TABLE_NAME: tableName,
                    ID_PARENT: toolbarId,
                    FORM2_CACHE_NAME: null
                }
            );
        }
    }

    addTableColumns(tableName, table) {

        const parentId = tableName + ".columns";
        this.storage._addRowToCache({
            ID: parentId,
            NAME: langDictionary.get("rights_name__columns"),
            TABLE_NAME: tableName,
            ID_PARENT: null,
            FORM2_CACHE_NAME: null
        });

        const originalEntityName = ORIGINAL_TABLE_NAME_BY_DUPLICATE_NAME[ tableName ];
        const langTableName = (
            table.langTableName
                || originalEntityName
                || tableName
        );
        let fields = [];
        if ( isArray(table.fields) ) {
            fields = table.fields;
        } else {
            const translatedFields = (
                langDictionary.FIELD_NAME[langTableName]
                || langDictionary.FIELD_NAME.root
            );

            const storage = RowsStorage.get(tableName);
            const originalStorage = RowsStorage.get(originalEntityName);

            //Обрабатывает поля для модулей которые указаны в excludeFields
            for (let key in table.excludeFields) {
                let columnName = table.excludeFields[key];
                if(columnName.module && system.modules[columnName.module]) {
                    table.excludeFields[key] = columnName.key;
                }
                if(columnName.notModule && !system.modules[columnName.notModule] ) {        
                    table.excludeFields[key] = columnName.key;
                }
            }

            for (const key in translatedFields) {
                const isKnownColumn = (
                    storage.columns.has(key)
                    || originalStorage.columns.has(key)
                );
                const isExcluded = (table.excludeFields || []).includes(key);

                if ( isKnownColumn && !isExcluded ) {
                    fields.push(key);
                }
            }
        }

        if ( system.hasModule("Voskhod") ) {
            this.addFieldsForVoskhod(fields, tableName);
        }

        if (
            tableName === "INVOICE_INCOMING"
            || tableName === "INVOICE_OUTGOING"
        ) {
            fields = fields.filter(
                (field) =>
                    field !== "LIST_TYPE_VAT_NAME" && field !== "LIST_VAT_NAME"
            );
        }
        // LW-9029 Просто добавим поля здесь, так как их нет в INVOICE
        if ( tableName === "INVOICE_OUTGOING" ) {
            fields.push("CONTRACTS_CLOSING_TYPE_NAME", "PLANNED_STATEMENT_DATE");
        }

        if (
            tableName === "INVOICE_INCOMING"
            || tableName === "INVOICE_OUTGOING"
        ) {
            fields.push("HAS_CLOSED_DOCS");
        }

        let rows = [];

        for (let i = 0, n = fields.length; i < n; i++) {
            let field = fields[i];

            if ( isString(field) ) {
                field = { key: field };
            }

            const { key } = field;

            // LW-4681, LW-4615 потребовался создать "костыль" для видимости полей в "Правах доступа" раздел "Столбцы"
            // т.к. стандартных методов этого сделать нет
            // LW-7030 Исправлен баг в "костыле". Он реализован через функцию this.fieldForInOutInvoiceIsNotExists(),
            // которая возвращает true, если поле отображать не нужно
            // Всё это нужно для полей входящих счетов, которых нет в исходящих, и наоборот.

            const isKeyID = key === "ID";

            if ( this.fieldIsNotExists(tableName, key) || isKeyID ) {
                continue;
            }

            let title = langDictionary.getFieldTitle(
                langTableName,
                key,
                false
            );

            if ( !title ) {
                continue;
            }

            rows.push({
                ID: parentId + "." + key,
                NAME: title,
                TABLE_NAME: tableName,
                ID_PARENT: parentId,
                FORM2_CACHE_NAME: null
            });
        }

        rows = rows.sort(function(rowA, rowB) {
            return rowA.NAME > rowB.NAME ? 1 : -1;
        });

        rows.forEach((row) => {
            this.storage._addRowToCache(row);
        });
    }


    fieldIsNotExists(tableName, key) {
        const fieldsNotExistsInInvoiceOutgoing = [
            "IS_CLOSING_DOC_TRANSFER",
            "IS_CLOSING_DOC_RECEIVE"
        ];

        const fieldsNotExistsInInvoiceIncoming = [
            "DATE_SEND_CLOSED_DOCS"
        ];

        const fieldsNotExistsInInvoiceOutgoingMoney = [
            "HAS_CLOSED_DOCS"
        ];

        const fieldsNotExistsInInvoiceIncomingMoney = [
            "HAS_CLOSED_DOCS"
        ];

        if ( system.hasModule("EuroAsia") ) {
            fieldsNotExistsInInvoiceIncoming.pop();
        }

        switch (tableName) {
            case "INVOICE_INCOMING":
                return fieldsNotExistsInInvoiceIncoming.includes(key);
            case "INVOICE_OUTGOING":
                return fieldsNotExistsInInvoiceOutgoing.includes(key);
            case "INVOICE_INCOMING_MONEY":
                return fieldsNotExistsInInvoiceIncomingMoney.includes(key);
            case "INVOICE_OUTGOING_MONEY":
                return fieldsNotExistsInInvoiceOutgoingMoney.includes(key);
            case "INVOICE":
                return (
                    fieldsNotExistsInInvoiceOutgoing.includes(key)
                        || fieldsNotExistsInInvoiceIncoming.includes(key)
                );
            default:
                return false;
        }
    }

    addTableFilters(tableName) {
        const parentId = tableName + ".filters";

        this.storage._addRowToCache({
            ID: parentId,
            NAME: langDictionary.get("rights_name__filters"),
            TABLE_NAME: tableName,
            ID_PARENT: null,
            FORM2_CACHE_NAME: null
        });

        this.storage._addRowToCache({
            ID: parentId + ".owner",
            NAME: langDictionary.get("rights_name__own_rows"),
            TABLE_NAME: tableName,
            ID_PARENT: parentId,
            FORM2_CACHE_NAME: null
        });

        this.storage._addRowToCache({
            ID: parentId + ".department",
            NAME: langDictionary.get("rights_name__department_rows"),
            TABLE_NAME: tableName,
            ID_PARENT: parentId,
            FORM2_CACHE_NAME: null
        });

        if ( tableName == "LIST_COMPANY" ) {
            let companyRolesParentId = parentId + ".companyRoles";
            this.storage._addRowToCache({
                ID: companyRolesParentId,
                NAME: langDictionary.get("rights_name__roles"),
                TABLE_NAME: tableName,
                ID_PARENT: parentId,
                FORM2_CACHE_NAME: null
            });

            getCompanyRoles().forEach(function(role) {
                this.storage._addRowToCache({
                    ID: companyRolesParentId + "." + role.id,
                    NAME: role.name,
                    TABLE_NAME: tableName,
                    ID_PARENT: companyRolesParentId,
                    FORM2_CACHE_NAME: null
                });
            }, this);
        }

        this.storage._addRowToCache({
            ID: parentId + ".other",
            NAME: langDictionary.get("rights_name__other"),
            TABLE_NAME: tableName,
            ID_PARENT: parentId,
            FORM2_CACHE_NAME: null
        });
    }

    addReportFilters(reportId) {
        // Как addTableFilters, но отдельно (lw-8759)
        this.storage._addRowToCache({
            ID: reportId,
            NAME: langDictionary.get("rights_name__filters"),
            ID_PARENT: null,
            TABLE_NAME: null,
            FORM2_CACHE_NAME: null
        });

        this.storage._addRowToCache({
            ID: reportId + ".owner",
            NAME: langDictionary.get("rights_name__own_rows"),
            ID_PARENT: reportId,
            TABLE_NAME: null,
            FORM2_CACHE_NAME: null
        });

        this.storage._addRowToCache({
            ID: reportId + ".department",
            NAME: langDictionary.get("rights_name__department_rows"),
            ID_PARENT: reportId,
            TABLE_NAME: null,
            FORM2_CACHE_NAME: null
        });

        this.storage._addRowToCache({
            ID: reportId + ".other",
            NAME: langDictionary.get("rights_name__other"),
            ID_PARENT: reportId,
            TABLE_NAME: null,
            FORM2_CACHE_NAME: null
        });
    }

    load(params, callback) {
        if ( !isFunction(callback) ) {
            callback = function() {
            };
        }

        const { tableName, appConfig } = params;
        const originalEntityName = ORIGINAL_TABLE_NAME_BY_DUPLICATE_NAME[ tableName ];
        const gridConfig = (
            allGridsConfig[originalEntityName]
            || allGridsConfig[tableName]
        ) || {};

        // на текущий момент так сложилось что разным пунктам меню может
        // соответсвовать одно entity с одной и той же формой
        // при этом настройки для них могут отличаться
        // а посему необходимо сбрасывать старые настройки
        this.storage.reset();

        const schemeName = originalEntityName || tableName;
        const table = deepMixin(gridConfig, appConfig);
        
        if ( schemeName in RowsStorage._scheme ) {        
            this.addTable(tableName, table);
        }

        let form2 = (
            appConfig.form2
            || gridConfig.form2
            || isFunction(appConfig.form) && appConfig.form.name
            || isFunction(appConfig.Form) && appConfig.Form.name
        );

        if ( isObject(form2) ) {
            if ( !form2.name ) {
                form2 = gridConfig.form2;
            } else {
                form2 = system.hasModule(form2.module)
                    ? form2.name
                    : gridConfig.form2;
            }
        }

        const cacheName = `${tableName}.${form2}`;
        const callbackParams = { cacheName };

        if ( isFunction(appConfig.form) ) {
            this.addForm(tableName, appConfig.form, cacheName, table);

            callback(callbackParams);
        }
        else if ( isFunction(appConfig.Form) ) {
            this.addForm(tableName, appConfig.Form, cacheName, table);

            callback(callbackParams);
        }
        else if ( form2 ) {
            let path = "apps/Form/forms/" + form2;
            if ( form2 == "RoleForm" ) {
                path = "classes/system/rights/form/RoleForm";
            }
            //@ts-ignore
            require([path], (FormClass) => {
                this.addForm(tableName, FormClass, cacheName, table);

                callback(callbackParams);
            });
        } else {
            callback(callbackParams);
        }
    }

    addForm(tableName, FormClass, cacheName = null, table) {
        const formName = FormClass.className || FormClass.name;

        system.trigger("beforeInitForm:" + formName, {
            Form: FormClass
        });

        const parentId = tableName + "." + formName;
        this.storage._addRowToCache({
            ID: parentId,
            NAME: langDictionary.get("rights_name__form"),
            TABLE_NAME: tableName,
            ID_PARENT: null,
            FORM2_CACHE_NAME: cacheName
        });

        const rows = this._form2rows({
            FormClass,
            parent: parentId,
            tableName,
            cacheName,
            table
        });

        rows.forEach((row) => {
            this.storage._addRowToCache(row);
        });
    }

    _form2rows(options) {
        const {
            FormClass,
            tableName,
            cacheName = null
        } = options;
        const parentId = options.parent;
        const formDefaults = FormClass.defaults;

        const elements = formDefaults
            ? formDefaults.elements
            : FormClass.prototype.elements();
        let rows = [];

        if ( !isArray(elements) ) {
            return rows;
        }

        let id;
        const tabParams = elements[0];
        if ( tabParams && tabParams.type == "Tabs" ) {
            if ( isArray(tabParams.head) && tabParams.head.length ) {
                id = parentId + ".head";
                rows.push({
                    ID: id,
                    NAME: langDictionary.get("rights_name__grid_head"),
                    ID_PARENT: parentId,
                    TABLE_NAME: tableName,
                    FORM2_CACHE_NAME: cacheName
                });

                rows = rows.concat(
                    this._formElements2rows(tabParams.head, {
                        FormClass,
                        parent: id,
                        tableName,
                        cacheName,
                        table: options.table
                    })
                );
            }

            const tabs = tabParams.tabs || [];
            tabs.filter(this.isEnabledByModule).forEach((tab) => {
                id = parentId + ".tab." + tab.title;
                const tabRow = {
                    ID: id,
                    NAME: langDictionary.getFormText(FormClass.className || FormClass.name, tab.title),
                    TABLE_NAME: tableName,
                    ID_PARENT: parentId,
                    FORM2_CACHE_NAME: cacheName
                };

                const tabRows = this._formElements2rows(tab.elements, {
                    FormClass,
                    parent: id,
                    tableName,
                    cacheName,
                    table: options.table
                });

                if ( tabRows.length ) {
                    rows.push(tabRow);

                    // Один инпут примечание на всю вкладку
                    if ( tabRows.length > 1 ) {
                        rows = rows.concat(tabRows);
                    }
                }
            });

            // LW-3915
            if ( tableName == "OPERATION_UNIT" ) { // Только для формы груза!
                const type = [
                    {
                        id: "Auto",
                        name: getLogOperationTypeName(1)
                    },
                    {
                        id: "Train",
                        name: getLogOperationTypeName(2)
                    },
                    {
                        id: "Sea",
                        name: getLogOperationTypeName(3)
                    },
                    {
                        id: "Air",
                        name: getLogOperationTypeName(4)
                    },
                    {
                        id: "Repack",
                        name: getLogOperationTypeName(5)
                    },
                    {
                        id: "Customs",
                        name: getLogOperationTypeName(6)
                    },
                    {
                        id: "Forward",
                        name: getLogOperationTypeName(7)
                    },
                    {
                        id: "Docs",
                        name: getLogOperationTypeName(8)
                    },
                    {
                        id: "other",
                        name: getLogOperationTypeName(9)
                    }
                ];

                for (let i = 0; i < type.length; i++) {
                    rows.push({
                        ID: parentId + ".tab." + type[i].id,
                        NAME: type[i].name,
                        ID_PARENT: parentId,
                        TABLE_NAME: tableName,
                        FORM2_CACHE_NAME: cacheName
                    });
                }
            }
        } else {
            rows = this._formElements2rows(elements, {
                FormClass,
                parent: parentId,
                tableName,
                cacheName,
                table: options.table
            });
        }

        let buttonsRows = this._formButtons2rows({
            FormClass,
            parent: parentId,
            tableName,
            cacheName
        });
        rows = rows.concat(buttonsRows);

        return rows;
    }

    _formElements2rows(elements, options) {
        const {
            FormClass,
            tableName,
            cacheName = null
        } = options;
        const parentId = options.parent;

        let rows = [];
        if ( !isArray(elements) ) {
            return rows;
        }

        let id;
        let subRows;
        elements.filter(this.isEnabledByModule).forEach((elemParams) => {
            if ( elemParams.type == "Group" ) {
                id = parentId + "." + elemParams.title;

                const groupRow = {
                    ID: id,
                    NAME: langDictionary.getFormText(FormClass.className || FormClass.name, elemParams.title),
                    TABLE_NAME: tableName,
                    ID_PARENT: parentId,
                    FORM2_CACHE_NAME: cacheName
                };

                subRows = this._formElements2rows(elemParams.elements, {
                    FormClass,
                    tableName,
                    parent: id,
                    cacheName
                });

                if ( subRows.length ) {
                    rows.push(groupRow);

                    // один грид в группе
                    if ( subRows.length > 1 ) {
                        rows = rows.concat(subRows);
                    }
                }

                return;
            }

            if ( elemParams.type == "Grid" || elemParams.type == "GridCheckbox" ) {
                const gridRows = this._formGrid2rows(elemParams, {
                    FormClass,
                    tableName,
                    parent: parentId,
                    cacheName
                });

                if ( !isArray(gridRows) || gridRows.length === 0 ) {
                    return;
                }

                rows = rows.concat(gridRows);

                // LW-3205 добавляем пункт в права (костыль)
                // LW-7498 нулевой элемент gridRows - необходимый костылю элемент
                const gridRow = gridRows[0];
                if ( gridRow.ID === "ORDER.OrderForm.tab.logistic_scheme.OPERATION" ) {
                    let createGridRow = {
                        ID: "ORDER.OrderForm.tab.logistic_scheme.OPERATION.add",
                        ID_PARENT: gridRow.ID_PARENT,
                        NAME: langDictionary.get("rights_name__create_log_oper"),
                        TABLE_NAME: gridRow.TABLE_NAME,
                        FORM2_CACHE_NAME: cacheName
                    };
                    rows.push(createGridRow);
                }

                return;
            }

            if ( elemParams.type == "Comments" ) {
                rows.push({
                    ID: parentId + "." + (elemParams.eid || "comments"),
                    NAME: langDictionary.get("rights_name__comments"),
                    TABLE_NAME: tableName,
                    ID_PARENT: parentId,
                    FORM2_CACHE_NAME: cacheName
                });

                return;
            }

            if ( elemParams.type == "CheckboxList" || elemParams.type == "CheckboxListFromArray" ) {
                let chelistRightId = elemParams.eid || (elemParams.link && elemParams.link.table);
                // invalid CheckboxList
                if ( !chelistRightId ) {
                    return;
                }

                rows.push({
                    ID: parentId + "." + chelistRightId,
                    NAME: langDictionary.getFormText(FormClass.className || FormClass.name, elemParams.title),
                    TABLE_NAME: tableName,
                    ID_PARENT: parentId,
                    FORM2_CACHE_NAME: cacheName
                });

                return;
            }

            if ( elemParams.type == "ArrivalPoints" ) {
                rows.push({
                    ID: parentId + "." + (elemParams.eid || "arrival_points"),
                    NAME: langDictionary.get("rights_name__log_oper_route"),
                    TABLE_NAME: tableName,
                    ID_PARENT: parentId,
                    FORM2_CACHE_NAME: cacheName
                });

                return;
            }

            if ( ELEMENTS_CLASSES.indexOf(elemParams.type) == -1 ) {
                return;
            }

            // invalid
            if ( !elemParams.key ) {
                return;
            }

            const EMPTY_EXCLUSIVE = RightsManager.BaseRights.EMPTY_EXCLUSIVE;
            const addExclusiveRoleForModule = (module) => {
                if (
                    module == "All"
                    && system.hasSomeModule(EMPTY_EXCLUSIVE[module].excludeModules)
                ) {
                    return;
                }

                if (
                    module in EMPTY_EXCLUSIVE
                    && tableName in EMPTY_EXCLUSIVE[module]
                    && EMPTY_EXCLUSIVE[module][tableName].includes(elemParams.key)
                ) {
                    rows.push({
                        ID: parentId + ".EMPTY_EXCLUSIVE--" + elemParams.key,
                        NAME:
                            this._translateElement(elemParams, {
                                FormClass,
                                tableName,
                            }) +
                            " " +
                            langDictionary.get("rights_name__empty_exclusive"),
                        TABLE_NAME: tableName,
                        ID_PARENT: parentId,
                        FORM2_CACHE_NAME: cacheName,
                    });
                }
            }

            Object.keys(system.modules).forEach(module => {
                addExclusiveRoleForModule(module);
            });
            addExclusiveRoleForModule("All");

            // LW-4681, LW-4615 потребовался создать "костыль" для видимости полей в "Правах доступа" раздел "Форма"
            // т.к. стандартных методов этого сделать нет
            // LW-7030 Исправлен баг в "костыле". Он реализован через функцию this.fieldIsNotExists(),
            // которая возвращает true, если поле отображать не нужно
            // Всё это нужно для полей входящих счетов, которых нет в исходящих, и наоборот.

            if ( this.fieldIsNotExists(tableName, elemParams.key) ) {
                return;
            }

            if ( elemParams.eid == "logistExportRates" && !(system.hasModule("Canavara") || system.hasModule("KTS")) ) {
                return;
            }

            if ( tableName === "REQUEST_CURRENCY_TRANSLATION" ) {
                const translatedElem = this._translateElement(elemParams, {
                    FormClass,
                    tableName
                });

                if ( !translatedElem ) {
                    return;
                }
            }
            // lw-11881: поля шапки формы в правах должны отображаться только для Коносаментов
            const isNotSeaForHBL = (elemParams.key == "HBL_NUMBER" || elemParams.key == "ID_PAYMENT_TYPE_HBL") && (tableName != "OPERATION_SUB_DOC_SEA" );
            if (isNotSeaForHBL) {
                return;
            }
            rows.push({
                ID: parentId + "." + elemParams.key,
                NAME: this._translateElement(elemParams, {
                    FormClass,
                    tableName
                }),
                TEXT_KEY: elemParams.textKey,
                KEY: elemParams.key,
                TABLE_NAME: tableName,
                ID_PARENT: parentId,
                FORM2_CACHE_NAME: cacheName
            });
        });

        if(options.table) {
            //Удаляет и обрабатывает поля для модулей которые указаны в excludeFields
            for (const key in options.table.excludeFields) {
                let columnName = options.table.excludeFields[key];

                if(columnName.module && system.modules[columnName.module] ) {        
                    columnName = columnName.key;
                }

                if(columnName.notModule && !system.modules[columnName.notModule] ) {        
                    columnName = columnName.key;
                }
               
                for (const row in rows) {
                    if(rows[row].ID.includes(columnName)) {
                        delete rows[row];
                    }
                }
            }
            //Фильтрует поля для модулей которые указаны в fields (файл runs.js)
            for (const key in options.table.fields) {
                let fieldColummn = options.table.fields[key];
                let columnName = null;

                if(fieldColummn.module && !system.hasModule[fieldColummn.module] ) {
                    columnName = fieldColummn.key;
                }

                if(fieldColummn.notModule && system.hasModule[fieldColummn.notModule] ) {
                    columnName = fieldColummn.key;
                }

                if(columnName == null) continue;

                rows = rows.filter(row =>((row.TEXT_KEY !== columnName) || (row.KEY !== columnName)));
            }
        }
        return rows;
    }

    _formGrid2rows(elemParams, options) {
        const gridTableName = elemParams.rightsTableName || elemParams.tableName;

        if ( !gridTableName ) {
            return;
        }

        const {
            FormClass,
            tableName,
            cacheName = null
        } = options;
        const parentId = options.parent;
        const gridId = parentId + "." + gridTableName;

        let title = langDictionary.getTableName(gridTableName);

        if ( elemParams.title ) {
            title = langDictionary.getFormText(FormClass.className || FormClass.name, elemParams.title);
        }

        const rows = [
            {
                ID: gridId,
                ID_PARENT: parentId,
                TABLE_NAME: tableName,
                NAME: title,
                FORM2_CACHE_NAME: cacheName
            }
        ];

        if ( isObject(elemParams.toolbar) && elemParams.toolbar.kit ) {
            const toolbarRows = this._formGridToolbar2rows(
                elemParams.toolbar,
                {
                    ...options,
                    parent: gridId
                }
            );

            return rows.concat(toolbarRows);
        }

        return rows;
    }

    _formGridToolbar2rows(toolbar, options) {
        const {
            tableName,
            cacheName = null
        } = options;
        const parentId = options.parent;

        const toolbarId = parentId + ".toolbar";

        const rows = [
            {
                ID: toolbarId,
                NAME: langDictionary.get("rights_name__toolbar"),
                TABLE_NAME: tableName,
                ID_PARENT: parentId,
                FORM2_CACHE_NAME: cacheName
            }
        ];

        return rows.concat(
            this._toolbarIcons2rows(
                {
                    toolbarId,
                    tableName,
                    cacheName,
                    kit: toolbar.kit
                }
            )
        );
    }

    _toolbarIcons2rows(options) {
        const {
            toolbarId,
            tableName,
            cacheName = null
        } = options;
        let { kit } = options;

        if ( !isArray(kit) ) {
            kit = toolbarConfig.kits[kit];

            if ( !isArray(kit) ) {
                return [];
            }
        }


        if ( kit.includes("trash") ) {
            kit.push("restore","fullRemove");
        }

        system.trigger("beforeCreateToolbarKit", tableName, kit);


        return kit.reduce((rows, iconName) => {
            const icon = toolbarConfig.icons[iconName];

            if ( isFunction(iconName) ) {
                iconName = iconName.name;

                rows.push({
                    ID: toolbarId + "." + iconName,
                    NAME: langDictionary.getIconTitle(iconName),
                    TABLE_NAME: tableName,
                    ID_PARENT: toolbarId,
                    FORM2_CACHE_NAME: cacheName
                });
                return rows;
            }

            if ( !isObject(icon) ) {
                return rows;
            }

            // search, filters, etc
            if ( icon.rights === false ) {
                return rows;
            }

            rows.push(
                {
                    ID: toolbarId + "." + iconName,
                    NAME: langDictionary.getIconTitle(iconName),
                    TABLE_NAME: tableName,
                    ID_PARENT: toolbarId,
                    FORM2_CACHE_NAME: cacheName
                }
            );

            return rows;
        }, []);
    }

    _formButtons2rows(options) {
        const {
            FormClass,
            tableName,
            cacheName = null
        } = options;
        const parentId = options.parent;
        const formDefaults = FormClass.defaults;
        const hasButtonsFunc = isFunction(FormClass.prototype.buttons);
        const buttons = [];
        let formButtons;

        let parentRow = {
            ID: parentId + ".buttons",
            ID_PARENT: parentId,
            TABLE_NAME: tableName,
            NAME: langDictionary.get("rights_name__form_buttons"),
            FORM2_CACHE_NAME: cacheName
        };

        if ( formDefaults ) {
            formButtons = formDefaults.buttons;
        } else if ( hasButtonsFunc ) {
            formButtons = FormClass.prototype.buttons();
        }
        formButtons = formButtons || [];
        formButtons.forEach(function(button) {
            if ( !button || !button.bid ) {
                return;
            }

            buttons.push({
                ID: parentRow.ID + "." + button.bid,
                ID_PARENT: parentRow.ID,
                TABLE_NAME: tableName,
                NAME: langDictionary.getFormText(FormClass.className || FormClass.name, button.title),
                FORM2_CACHE_NAME: cacheName
            });
        });

        if ( buttons.length ) {
            buttons.unshift(parentRow);
        }

        return buttons;
    }

    _translateElement(elemParams, options) {
        let FormClass = options.FormClass;
        let title = elemParams.title;
        let key;

        if ( elemParams.type == "GridList" ) {
            key = elemParams.textKey || elemParams.mainField || elemParams.key;
        } else {
            key = elemParams.key;
        }

        if ( elemParams.type == "File" || elemParams.type == "Photo" || elemParams.type == "Textarea" ) {
            return langDictionary.getFormText(FormClass.className || FormClass.name, elemParams.key);
        }

        // супер костыль
        if ( key == "LIST_WAREHOUSE_COORDS" ) {
            return langDictionary.getFieldTitle(options.tableName, "COORDS_ADDRESS");
        }

        if ( isString(title) ) {
            return langDictionary.getFormText(FormClass.className || FormClass.name, title);
        }

        if ( key && !title && title !== false ) {

            const originalTableName = ORIGINAL_TABLE_NAME_BY_DUPLICATE_NAME[options.tableName];
            const title = langDictionary.getFieldTitle(options.tableName, key)
                ?? langDictionary.getFieldTitle(originalTableName, key);
            return title;
        }

        return title || key || "";
    }

    isEnabledByModule(element) {
        return !element.module || system.hasModule(element.module);
    }
}
